/**
 * Workflow Status Model
 * Represents a dynamic workflow status that can be system-defined or user-defined
 * Based on new workflow_statusses table structure
 */

export interface IWorkflowStatus {
	/** Unique identifier for the workflow status */
	workflowStatusId: string;

	/** Display name/description of the status */
	name: string;

	/** Font Awesome icon array [style, icon-name] */
	icon: string[];

	/** Ionic color or hex color */
	color: string;

	/** Sort order for displaying statuses (10, 20, 30, 40, 50 for system, between for custom) */
	sortOrder: number;

	/** Order type: 1 = workshop, 2 = sales */
	orderType: number;

	/** Whether this is a system-defined status (cannot be deleted, description can be changed) */
	isSystem: boolean;

	/** Whether to trigger stock mutation (0 or 1) */
	isStockMutation: boolean;

	/**
	 * Time registration type - API handles automatic transitions:
	 * - 0: API moves order to to_workflow_status_id when clocking starts
	 * - 1: Can clock and stay, OR API moves to to_workflow_status_id if set
	 * - 9: Requires confirmation before moving to this status (API stops clocking)
	 */
	timeRegistrationType: 0 | 1 | 9;

	/** Whether vehicle must be present (0 or 1) */
	isVehicleHere: boolean;

	/** Email template ID to send on status change */
	emailTemplateId?: string;

	/** Whether this status is archived (archived statuses are hidden) */
	isArchived: boolean;

	/** Target status ID for automatic transitions (used by time_registration_type 0 and 1) */
	toWorkflowStatusId?: string;

	/** Target status ID to move the order to when clocking is stopped on this status */
	toWorkflowStatusIdAfterClocking?: string;

	/** Count of workorders in this status (optional, for badge display) */
	count?: number;

	/** Whether this status has been visited/loaded (used for accurate count display) */
	isVisited?: boolean;
}

/**
 * Workflow Status DTO from NEW API endpoint (workflow_statusses table)
 * GET only - app cannot modify statuses
 *
 * ICON HANDLING:
 * - Icons are provided by API in format "fal fa-eye" (FontAwesome style + icon name)
 * - Client parses to array format ["fal", "eye"] for fa-icon component
 * - Falls back to client-side determination if not provided
 * - All Pro Light icons are available since entire pack is loaded in app.module.ts
 */
export interface IWorkflowStatusDto {
	workflow_status_id: string;
	description: string;
	icon?: string | null; // e.g., "fal fa-eye" from API
	sort_order: number;
	order_type: number; // 1 = workshop (inkoop), 2 = sales (verkoop)
	is_system: number; // 0 or 1
	is_stock_mutation: number; // 0 or 1
	time_registration_type: 0 | 1 | 9 | null; // 0=none, 1=start, 9=always stop
	is_vehicle_here: number; // 0 or 1
	is_blocked: number; // 0 or 1
	is_archived: number; // 0 or 1
	to_workflow_status_id?: string | null;
	to_workflow_status_id_after_clocking?: string | null;
	owner_id: string;
	organization_id: string;
	created_by: string;
	modified_by?: string | null;
	created_date: string;
	modified_date?: string | null;
}

/**
 * System status sort orders (cannot be used by custom statuses)
 * Workshop: Afspraak=10, Inspectie=20, Offerte=30, Onder handen=40, Gereed=50
 * Sales: Offerte=10, Orderbevestiging=20, Rijklaar maken=30, Af te leveren=40, Afgeleverd=50
 */
export enum SystemStatusSortOrder {
	// Workshop
	WORKSHOP_APPOINTMENT = 10,
	WORKSHOP_INSPECTION = 20,
	WORKSHOP_QUOTE = 30,
	WORKSHOP_IN_PROGRESS = 40,
	WORKSHOP_READY = 50,

	// Sales
	SALES_QUOTE = 10,
	SALES_ORDER_CONFIRMATION = 20,
	SALES_PREPARATION = 30,
	SALES_TO_DELIVER = 40,
	SALES_DELIVERED = 50
}

/**
 * Workflow Status fields for API requests
 */
export enum WorkflowStatusField {
	WorkflowStatusId = "workflow_status_id",
	Description = "description",
	Icon = "icon",
	SortOrder = "sort_order",
	OrderType = "order_type",
	IsSystem = "is_system",
	IsStockMutation = "is_stock_mutation",
	TimeRegistrationType = "time_registration_type",
	IsVehicleHere = "is_vehicle_here",
	IsBlocked = "is_blocked",
	IsArchived = "is_archived",
	ToWorkflowStatusId = "to_workflow_status_id",
	ToWorkflowStatusIdAfterClocking = "to_workflow_status_id_after_clocking",
	OwnerId = "owner_id",
	OrganizationId = "organization_id",
	CreatedBy = "created_by",
	ModifiedBy = "modified_by",
	CreatedDate = "created_date",
	ModifiedDate = "modified_date"
}

/**
 * API Response for single workflow status
 */
export interface IWorkflowStatusResponse {
	data: IWorkflowStatusDto[];
	maxPages: number;
}

/**
 * API Response for multiple workflow statuses
 */
export interface IWorkflowStatusesResponse {
	currentPage: number;
	data: IWorkflowStatusDto[];
	maxPages: number;
	nextpage: boolean;
}

/**
 * Time registration types for workflow statuses
 * Users can always start clocking - the API handles automatic status transitions
 */
export enum TimeRegistrationType {
	API_AUTO_MOVE = 0, // API automatically moves order to to_workflow_status_id when clocking starts
	ALLOWED_WITH_AUTO_MOVE = 1, // Can clock and stay, OR API moves to to_workflow_status_id if configured
	STOP_WITH_CONFIRMATION = 9 // Requires confirmation alert before moving TO this status, API stops clocking
}
